import type { FunctionalComponent, HTMLAttributes, Ref, VNodeProps } from 'vue'
import type { LocationQuery, RouteParams, RouteRecordName } from 'vue-router'
import type { ShortLink, Store } from '../views/contests/Components/types/global'
import type { storeInterface } from './store'
import type { RadioCardOption as RadioOption } from '@/components/ui/AppRadioCard.vue'
import type { ContentCreator, ContentScope } from '@/types/contentScope'
import type { Icons } from '@/components/base/BaseIcon.vue'
import type { ChipColor } from '@/components/base/BaseChip.vue'
import type { RuleParameters } from './audience'
import type { AccessCondition, ApplyReductionOn } from './offer'
import type { ContestMechanic } from './global'
import type { ColumnDef } from '@/components/ui/Table/interfaces'

export type Autopilot = {
  id: number
  name: string
  trigger: string
  messageContent: string
  bonusPoints: number
  conversionDelayInHours: number
  notificationDelayInMinutes: number
  notificationSendingTime?: string
  isActive: boolean
  countStores: number
  countMessages: number
  countConversions: string
  stores: AutopilotStore[]
  isTemplate: boolean
}

type AutopilotStore = {
  id: number
  merchantId: number
  name: string
  smsName: string
  slug: string
  isEshop: boolean
  businessCrmType: string
  businessCrmIdentifier: number
  isActive: boolean
  storeTypeId: number
}

export type AutopilotType = {
  autopilotsTypeFrequencies: string[]
  autopilotsTypeReviews: string[]
}

export type ButtonLink = {
  name?: RouteRecordName
  external?: string
  params?: RouteParams
  query?: LocationQuery
}

export type User = {
  id: number
  name: string
  email: string
  status: number
  jobTitle: string
  phone?: string
  passwordSend?: false
  lastLoginDate?: string
  role_template_id?: number
  roleTemplate?: RoleTemplate
  merchant: UserMerchant
} | null

export type JwtToken = {
  jwt: {
    accessToken: string
  }
}

export type TabletVersionStatus = 'up_to_date' | 'outdated_ota' | 'outdated_native' | 'unsupported' | 'unknown'

export type Tablet = {
  id: number
  storeId: number
  storeName?: string | null
  merchantId: number
  merchantName?: string | null
  udid: number
  canAssociatePartnerDevice: boolean
  posDeviceId?: number | null
  isCustomerProfileConsultationMode: boolean
  platformShort: string
  applicationVersion?: string
  latestApplicationVersion?: string | null
  versionStatus?: TabletVersionStatus
  canRemoteUpdate?: boolean
  lastLoginAt?: Date
  lastCheckinAt: Date
  lastPingAt: Date
  createdAt: string
  updatedAt: string
  partnerIntegration: PartnerIntegration[] | null
  partnerDevice: PartnerIntegration | null
}

export type OrderTabletFlow = {
  orderId: number
  orderNumber: string | null
  orderedAt: string | null
  receivedByWebhookAt: string
  webhookDelayMs: number | null
  sentToTabletAt: string
  receivedByApiAt: string | null
  ackLatencyMs: number | null
  checkinAt: string | null
  checkinPoints: number | null
  checkinIsValid: boolean | null
  checkinDelayMs: number | null
}

export type TabletEventRange = '24h' | '7j' | '30j'

export type TabletLifecycleEvent = {
  eventType: 'connected' | 'disconnected' | 'flapping'
  timestamp: string
  disconnectReason: string | null
  ipAddress: string | null
  // Set only when eventType === 'flapping': a throttled summary row standing in for
  // this many connect/disconnect toggles observed in a single burst (backend#9444).
  flapCount: number | null
  // Set only when eventType === 'flapping': which way the tablet was toggling when the
  // burst crossed the threshold (backend#9444).
  lastState: 'connected' | 'disconnected' | null
}

export type TabletDeviceEvent = {
  eventType:
    | 'added'
    | 'changed'
    | 'removed'
    | 'tablet_created'
    | 'tablet_updated'
    | 'tablet_deleted'
    | 'tablet_config_patched'
    | 'tablet_identify_requested'
    | 'tablet_refresh_config_requested'
    | 'tablet_update_requested'
    | 'tablet_autologin'
    | 'tablet_offline_network'
    | 'tablet_offline_app_background'
    | 'tablet_offline_unknown'
  previousDeviceId: string | null
  deviceId: string | null
  previousDeviceName?: string | null
  deviceName?: string | null
  transferredToTabletId?: number | null
  transferredFromTabletId?: number | null
  timestamp: string
  source: 'tablet' | 'manual' | 'reconciliation'
  actorName: string | null
  // Only set for 'tablet_updated'/'tablet_config_patched' — null otherwise.
  metadata: Record<string, unknown> | null
}

export type PartnerIntegration = {
  id: number
  publicId: string
  partnerDeviceId: number
  partnerDeviceName: string
  partnerId: number
  storeId: number
  partner: Partner
  isActive: boolean
  isHardwareless: boolean
  type: string
  shortLink: ShortLink
  lastMagicscanOrderReceivedAt: string
}

export type RoleTemplate = {
  id: number
  slug: string
}

export type UserMerchant = {
  id: number
  name: string
  smsName: string
  employeeTabletCode: string
  webAppUrl?: string
  webappLink?: string
  slug?: string
  merchantActivityId?: number
  hasCompatibleVoucherIntegration?: boolean
  createdAt?: string
}

export type MerchantAttributes = {
  id: string
  type: string
  label: string
  availableValues?: string[]
}

export type TemplateLayout = 'emptyState'

export type TemplateData = {
  id: number
  name: string
}

export type TemplateDetail = {
  description: string
  id: number
  name: string
  prefilledDate?: string
  templatable: any
}
export type TemplateDetailContests = {
  contestGameRewards: ContestGameReward[]
  title: string
  subtitle: string
  canLose: boolean
  mechanic?: ContestMechanic
  losingProbability?: number
  losingTileText?: string
  formTitle: string
  formSubtitle: string
  formSubmitButtonText: string
  optinText: string
}
export type ContestTemplateReward = ContestGameReward & {
  rewardType?: { slug: RewardTypeSlug }
}
export type TemplateDetailContestsResponse = Omit<TemplateDetailContests, 'contestGameRewards'> & {
  contestGameRewards: ContestTemplateReward[]
  contestGameBoostType?: { slug: string }
  daysBetweenParticipations?: number
}
export type AuthFormData = {
  email: string
  password?: string
  rememberMe?: boolean
  confirmPassword?: string
}
export type AuthInputErrors = {
  email: string[]
  password: string[]
  confirmPassword: string[]
}
export type TemplateModule =
  | 'analytics'
  | 'review'
  | 'rewards'
  | 'survey-question'
  | 'campaign'
  | 'customer'
  | 'contests'
  | 'exports'
  | 'import'
  | 'missedCalls'
  | 'settings.accountPage'
  | 'loyalty'
  | 'tablet'
  | 'wallet'
  | 'legacy-contests'

export type Webhook = {
  storeId: number
  url: string
  hash: string
}

export type KeyType = 'merchants' | 'stores' | 'users'

export type GlobalConfig = {
  merchant: Record<string, any>
  user: Record<string, any>
  store: Record<string, any>
}

export type TextTargetTypes = {
  classic: string
  formal: string
}

export type DefaultPartnerParameters = {
  pointsClaimingTitle: TextTargetTypes
  pointsClaimingSubtitle: TextTargetTypes
  failedTitle: TextTargetTypes
  failedSubtitle: TextTargetTypes
  isFormal: boolean
  minimumAmount: number
  pointsByCurrencyUnit: number
  pointsClaimingDelay: number
  partnerStores: PartnerStore[]
}

export interface hasQrCodeLoyalty {
  hasQrCodeLoyalty: boolean
  handleQrCodeLoyalty: () => void
}
export type PartnerParameter = {
  id: number
  name: string
  slug: string
  publicId: string
  howToUrl: string | null
  hasCustomApiUrl: boolean
  isActive: boolean
  isApiTokenGlobal: boolean
  partner: Partner
  merchant: UserMerchant
  isProductSyncEnabled: boolean
  isProductPriceSyncEnable: boolean
  pointsByCurrencyUnit: number | null
  pointsClaimingDelay: number | null
  minimumAmount: number | null
  fixedPointsAmount: number | null
  messageTemplate: MessageTemplate | null
  messageChannelId: number | null
  apiUrl: string | null
  apiToken: string | null
  apiUsername: string | null
  authenticationUrl: string | null
  webhookUrl: string | null
  webhooks: Webhook[]
  stores: AssociatedPartnerStores[] | null
  partnerStores: PartnerStore[] | null
  lastProductsSyncAt: string | null
  merchantIdFromPartner: number | null
  associatedSiouplaitIds?: Array<string>
  pointsClaimingTitle: string | null
  pointsClaimingSubtitle: string | null
  failedTitle: string | null
  failedSubtitle: string | null
  qrCodeLoyalty: boolean
  logo: string
}

export type Partner = {
  id: number
  name: string
  slug: string
  backgroundColor: string
  buttonColor: string
  textsColor: string
  introduction: string | null
  logo: string
  token: string | null
  isVisibleInMarketplace: boolean
  isAvailableInMarketplace: boolean
  isUsingOpenApi: boolean
  type: string
  hasCustomApiUrl: boolean
  hasEndpointToGetProducts: boolean
  hasEndpointToGetStores: boolean
  hasSegmentationSupport: boolean
  hasConsumedProductsSupport: boolean
  hasRewardSupport: boolean
  hasRewardCartSupport: boolean
  isMerchantAssociationRequired: boolean
  isMonoStoreAssociation: boolean
  usePartnerStoreHash: boolean
  apiUrl: string | null
  authenticationUrl: string | null
  howToUrl: string | null
  partnerAuthenticationType: {
    id: number
    slug: PartnerAuthenticationTypeSlug
  }
  partnerText: {
    api_url_field_label: string | null
    api_token_field_label: string | null
    api_username_field_label: string | null
  } | null
  hasPromotionalCodeSupport: boolean
  sendVoucherToPartner: boolean
  features: {
    customersSync: boolean
    loyaltyPointsImportFromPartner: boolean
    groupedItems: boolean
  }
  accountCount: number
  accessTokenCount: number
  partnerDeviceId?: string
  isApiTokenGlobal?: boolean
  isActive?: boolean
  partner?: PartnerParameter
  stores?: PartnerStore[]
}

export type PartnerAuthenticationTypeSlug =
  | 'password'
  | 'oauth'
  | 'token'
  | 'time-limited-token'
  | 'basic-auth'
  | 'internal-token'
  | 'internal'

export type PartnerUserAuth = {
  login: string
  password: string
}

export type OauthAccessToken = {
  user: User
  createdAt: string
}

export interface AssociatedPartnerStores extends PartnerStore {
  merchantId: number
  smsName: string
  slug: string
  businessCrmType?: any
  businessCrmIdentifier?: any
  isActive: boolean
  storeTypeId?: any
}

export interface PartnerStore {
  partner_id: string
  id: number
  name: string
  storeId: number
  storeName: string
  hash?: string
  storeIdFromPartner: string
  storeNameFromPartner: string
}

export interface Item {
  id?: number
  name: string
  externalId: string
  partnerId: number
  type: string
}

export interface PartnerItem {
  id: string | number
  name: string
  type: 'product' | 'menu'
}

export interface PartnerProduct {
  id?: string | number
  externalId: string
  name: string
  type: 'product' | 'menu' | 'manual'
  partnerId: number | null
  description?: string
  price?: number
  image?: string
}

export interface AssociatedExternalStores extends PartnerStore {
  storeIdFromPartner: string
}

export interface PartnerParametersPartnerStores {
  id: number
  storeId: number
  partnerId: number
  partnerApiToken: string
  storeIdFromPartner: string
  storeNameFromPartner?: string
  partnerParsing?: unknown
  customerNotificationChannelId?: number
}

export interface Country {
  alpha2Code: string
  flag?: string
  id?: number
  name?: string
  phoneLength?: number
  phonePrefix?: number
}

export interface AgesFilter {
  min: number
  max: number
  value: number[]
}

export interface LastCheckinDateFilter {
  from: string
  to: string
}

export interface RegisterDateFilter {
  from: string
  to: string
}

export interface FilterTag {
  type: string
  id: number
  color: string
  name: string
}

export interface CustomerFilter extends Record<string, unknown> {
  ages: AgesFilter
  frequencyTypes?: any[]
  genders: any[]
  hasOptin: any[]
  lastCheckinDate: LastCheckinDateFilter
  registerDate: RegisterDateFilter
  tags: FilterTag[]
  segmentIds: number[]
  postalCodes: string[]
}

export const defaultFilter = {
  ages: {
    min: 0,
    max: 100,
    value: [0, 100]
  },
  genders: [],
  hasOptin: [],
  lastCheckinDate: {
    from: '',
    to: ''
  },
  registerDate: {
    from: '',
    to: ''
  },
  tags: [],
  segmentIds: [],
  postalCodes: []
} as CustomerFilter

export interface CustomerCreation {
  phone: string
  points: number
  referenceStore: {
    id: number
    name: string
  }
  country: string
  loyaltyCardNumber: string | null
  email: string
  optin: boolean | null
  nationalNumber: string | null
  hasOptinEmail: boolean | null
  postalCode: string | null
  city: string | null
  hasCheckin: boolean
  firstname: string
  lastname: string
  gender: number
  birthday: string | null
  birthdayFormatted: string | null
  tags: string[]
  lists: MerchantList[]
}

export interface MerchantList {
  id: number
  name: string
  merchantId: number
  createdAt: string
  updatedAt: string
  type?: string
}

export interface CustomerCreationErrors {
  phone: string | null
  referenceStore: string | null
  email: string | null
  loyaltyCardNumber: string | null
  points: string | null
  birthday: string | null
  postalCode: string | null
  city: string | null
}

export interface CustomerListCountResponse {
  data: CustomerListCountResponseData
}

export interface CustomerListCountResponseData {
  count: number
}

export interface SortTableCustomerList {
  sortBy: string
  sortOrder: 'DESC' | 'ASC'
}

export interface PaginationTableCustomerList {
  currentPage: number
  lastPage?: number
  total?: number
  pageSize?: number
}

export interface CustomerListColumn extends ColumnDef {
  id: string
  label?: string
  type?: string
  hideOnMobile?: boolean
  hideOnDesktop?: boolean
}

export interface ColumnsTableCustomerList {
  selection?: CustomerListColumn
  customer: CustomerListColumn
  customerName?: CustomerListColumn
  lastCheckin: CustomerListColumn
  checkins: CustomerListColumn
  points: CustomerListColumn
  audience: CustomerListColumn
}

export interface CustomerListTable {
  sort: SortTableCustomerList
  pagination: PaginationTableCustomerList
  columns: ColumnsTableCustomerList
  rows: RowTableCustomerList[]
}

export interface CustomerList {
  id: string
  phone: number
  name: string | null
  email: string | null
  loyaltyCardNumber: string | null
  gender: string | null
  hasOptin: boolean
  countryFlag: string
}

type Gender = 'man' | 'woman' | 'undisclosed' | 'other' | null

export type CustomerGender = {
  id: number
  slug: Gender
  text: string
}

export interface RowTagTableCustomerList {
  id: number
  label: string
  type: string
  color: string
  icon: string
}

export interface RowTypeTableCustomerList {
  colorHexa: string
  label: string
  svg?: string[]
}

export interface RowDataTableCustomerList {
  customer: CustomerList
  customerName: { value: string; hideOnDesktop: boolean }
  lastCheckin: {
    date: string
    time: string
    hideOnMobile: boolean
  }
  checkins: { value: number; hideOnMobile: boolean }
  points: { value: number; hideOnMobile: boolean }
  audience: { items: RowTagTableCustomerList[]; hideOnMobile: boolean }
}

export interface RowTableCustomerList {
  id: number
  data: RowDataTableCustomerList
}

export interface FetchOptions {
  requestParams?: any
  refetch?: boolean
  immediate?: boolean
}

export interface FetchOptions {
  requestParams?: any
  refetch?: boolean
  immediate?: boolean
}

type SuccessPageStepSlugs = 'google' | 'contacts' | 'visual-identity' | 'welcome'
type SuccessPageGoalSlugs =
  | 'google'
  | 'trustpilot'
  | 'followers'
  | 'loyalty-program'
  | 'missed-calls'
  | 'ghost-traffic'
  | 'no-objective'

export interface SuccessSteps {
  id: number
  slug: SuccessPageStepSlugs
  title: string
  description: string
  estimatedTimeInMinutes: number
  isDone: boolean
  buttons: SuccessButtons
  extra?: StepExtra
}

export interface GoalSlugsMapping {
  slug: SuccessPageGoalSlugs
  stepSlugs: SuccessPageStepSlugs[]
  postIds: number[]
  faqIds: number[]
  shortcuts: ShortcutsGoalsSlugByType
}

interface ShortcutsGoalsSlugByType {
  physical: number[]
  online: number[]
  omnicanal: number[]
}

interface ShortcutContent {
  id?: number
  name?: string
  link?: string
  path?: string
  image?: string
  html?: string
  idTemplate?: number
  type?: TemplateModule
  buttonText?: string
}

export type ShortcutType = 'template' | 'tip' | 'redirection'

export interface link {
  id: number
  title: string
  link: string
  thumbnail?: string
  estimatedTimeInMinutes?: number
}

export interface linkAccountManager {
  id: number
  path: string
}

export interface CommunityLink {
  id: number
  link: string
  name: string
  icon?: string
  emoji?: string
  color?: string
}

export interface Survey {
  id: number
  choices: ChoiceSurvey[]
  createdAt: string
  launchDate: number
  endDate: number
  questionText: string
  updatedAt: string
  isTerminated: boolean
  isActive: boolean
  customerAnswersCount: number
  statistics?: SurveyStatistic
  stores?: Store[]
}

export interface SurveyStatistic {
  surveyedQuantity: number
  answeredQuantity: number
  results: SurveyStatisticResult[]
}

export interface SurveyStatisticResult {
  choiceId: number
  count: number
}

export interface SurveyTemplate {
  choices: ChoiceSurvey[]
  questionText: string
  title: string
  description: string
}

export interface ChoiceSurvey {
  id: number
  text: string
  answersCount?: number
  answersPercentage?: number
  isHighest?: boolean
}

export interface SuccessPageShortcut {
  id: number
  title: string
  name: string
  type: ShortcutType
  content: ShortcutContent
  hasImage?: boolean
  buttonText?: string
}

interface SuccessButtons {
  primary: StepButton
  secondary: StepButton
}

export interface StepButton {
  text: string
  type: string
  value: string
}

export interface StepExtra {
  type: string
  content: StepContent
}

interface StepContent {
  embed?: string
  query?: { [key: string]: string }
  text?: string
}

export interface RewardType {
  id: number
  slug: string
  name: string
  isVoucher: boolean
}

export type RewardConditionType = 'cart_minimum_in_cents' | 'cart_item_count' | 'required_products'

export type RewardConditionOperator = 'eq' | 'gt' | 'gte' | 'lt' | 'lte' | 'contains_all' | 'contains_any'

export interface RewardConditionProductsItem {
  externalId: string
  quantity: number
}

export interface RewardConditionProducts {
  items: RewardConditionProductsItem[]
  excludedExternalId?: string[]
}

export interface RewardCondition {
  type: RewardConditionType
  operator: RewardConditionOperator
  value?: number
  products?: RewardConditionProducts
}

export interface RewardClaimed {
  id: number
  date: string
  customer: CustomerDetails
  store: storeInterface
  spentPoints: number
  hash: string
  reward: Reward
  count?: string | number
  isUsed?: boolean
  isValid?: boolean
  couponCode?: string
  retrievedAtFormatted?: { date: string; time: string }
  validityDateFormatted?: { date: string; time: string }
  validityInDays?: number | null
  expiredAt?: Date
}

export interface Reward {
  id: number
  name: string
  pictureUrl: string
  points: number
  canClaim: boolean
  text: string
}

export type RewardUsage =
  | 'loyalty'
  | 'marketing_flow'
  | 'autopilot'
  | 'campaign_sms'
  | 'campaign_push'
  | 'contest'
  | 'offer'

export interface BaseReward {
  id: number | null
  name: string
  description?: string | null
  externalId?: string
  pictureUrl: string
  value: number | null
  validityInDays: number | null
  minimumOrderAmount: number | null
  voucherDiscountAmount: number | null
  discountValuePercent: number | null
  isForSingleUse: boolean
  rewardType: RewardType
  storeIds: number[]
  rewardTypeId: number
  items: PartnerProduct[]
  stores?: RewardStoreInterface[]
  hasReachedClaimLimit?: boolean
  source?: 'loyalty' | 'marketing_flow' | 'campaign' | 'contest_game' | 'offer'
  usages?: RewardUsage[]
  isActive?: boolean
  includedSegmentIds?: number[]
  excludedSegmentIds?: number[]
  conditions?: RewardCondition[]
  applyReductionOn?: ApplyReductionOn
  accessCondition?: AccessCondition
  requiredProductExternalIds?: string[]
  cartItemCount?: number | null
  targets?: { sku: string; quantity?: number }[]
  targetSkus?: string[]
  target_skus?: string[]
}

export interface LoyaltyReward extends BaseReward {
  points: number | null
  claimLimitQuantity: number | null
  claimLimitDays: number | null
  notificationTextOverride?: string
  lists: any[]
  segments: Segment[]
  includedSegments: Segment[]
  excludedSegments: Segment[]
  autopilots: LoyaltyRewardAutopilot[]
}

export interface RewardStoreInterface {
  id: number
  name: string
  associatedWithCurrentUser?: boolean
}

export interface RewardPayload {
  id?: number | null
  name?: string
  description?: string
  rewardTypeId?: number
  externalId?: string | null
  source?: string | null
  usages?: RewardUsage[]
  storeIds?: number[] | null
  targets?: { sku: string; quantity?: number }[]
  value?: number | null
  minimumOrderAmount?: number | null
  validityInDays?: number | null
  voucherDiscountAmount?: number | null
  discountValuePercent?: number | null
  claimLimitQuantity?: number | null
  claimLimitDays?: number | null
  isForSingleUse?: boolean
  conditions?: RewardCondition[]
  pictureUrl?: string | null
  items?: { externalId: string; partnerId: number; name: string; type: string; image?: string; id?: string | number }[]
  notificationTextOverride?: string
  points?: number | null
}

export interface OfferStore {
  id: number
  name: string
}

export interface LoyaltyRewardAutopilot {
  id: number
  name: string
}

export interface OfferAvailabilityTimeRange {
  start: string
  end: string
}

export interface OfferAvailabilitySlot {
  daysOfWeek: number[]
  timeRanges: OfferAvailabilityTimeRange[]
}

export type OfferChannel = 'pos' | 'web' | 'touchscreen' | 'kiosk' | 'online' | 'click_and_collect' | 'delivery'

export interface Offer {
  id: number
  publicId?: string
  name: string
  description?: string
  rewardId: number
  reward?: LoyaltyReward
  startDate?: string | null
  endDate?: string | null
  loyaltyProgramMemberOnly?: boolean
  status?: 'scheduled' | 'active' | 'expired'
  channels?: OfferChannel[]
  includedSegments?: Audience[]
  excludedSegments?: Audience[]
  availabilitySlots?: OfferAvailabilitySlot[]
  storeIds?: number[]
  stores?: OfferStore[]
  claimLimitQuantity?: number | null
  claimLimitDays?: number | null
  isPromotionalCodeRequired?: boolean
  scope?: ContentScope | null
  creator?: ContentCreator | null
}

export interface OfferCreatePayload {
  scope?: ContentScope
  rewardId: number
  name: string
  description?: string
  startDate: string
  endDate?: string | null
  loyaltyProgramMemberOnly?: boolean
  channels?: OfferChannel[]
  includedSegmentIds?: number[]
  excludedSegmentIds?: number[]
  availabilitySlots?: OfferAvailabilitySlot[]
  storeIds?: number[]
  claimLimitQuantity?: number | null
  claimLimitDays?: number | null
  isPromotionalCodeRequired?: boolean
}

export interface OfferUpdatePayload {
  scope?: ContentScope
  rewardId?: number
  name?: string
  description?: string
  startDate?: string
  endDate?: string | null
  loyaltyProgramMemberOnly?: boolean
  channels?: OfferChannel[]
  includedSegmentIds?: number[]
  excludedSegmentIds?: number[]
  availabilitySlots?: OfferAvailabilitySlot[]
  storeIds?: number[]
  claimLimitQuantity?: number | null
  claimLimitDays?: number | null
  isPromotionalCodeRequired?: boolean
}

export type PromotionalCodeGenerationType = 'global' | 'unique'

export interface OfferPromotionalCode {
  id: number
  offerId: number
  value: string
  uses: number
  usageLimit: number
  createdAt: string
  updatedAt: string
}

export interface OfferGeneratePromotionalCodesPayload {
  slug: string
  generationType: PromotionalCodeGenerationType
  usageLimit: number
  quantity?: number
}

export interface Segment {
  id: number
  name: string
  type: 'static' | 'dynamic'
  merchant_id: number
  customer_count: number | null
}

export interface MerchantColors {
  background: string
  button: string
  text: string
}

export interface LoyaltyProgram {
  cguUrl: string
  cguWording: string
  id: number
  privacyPolicyUrl: string
}

export interface MerchantTexts {
  usePoliteLanguage?: boolean
  homeTitle?: string
  homeSubtitle?: string
  homeButton?: string
  homeButtonWithActiveIntegration?: string
  pointsTitle?: string
  pointsSubtitle?: string
  pointsLabel?: string
  integrationPointsClaimingTitle?: string
  integrationPointsClaimingSubtitle?: string
  integrationFailedTitle?: string
  integrationFailedSubtitle?: string
  integrationAfterOrderMailSubject?: string
  integrationAfterOrderMailBody?: string
  integrationAfterOrderSms?: string
  rewardsTitle?: string
  rewardsSubtitle?: string
  rewardsSubtitleNew?: string
  rewardsText?: string
  rewardsButton?: string
  rewardsPopupConfirmTitle?: string
  rewardsPopupConfirmSubtitle?: string
  rewardsPopupForbiddenTitle?: string
  congratsTitle?: string
  congratsSubtitle?: string
  congratsSubtitleSms?: string
  congratsSubtitleSmsVoucher?: string
  congratsSms?: string
  voucherSms?: string
  congratsSmsNew?: string
  loginTitle?: string
  loginSubtitle?: string
  questionsOptin?: string
  questionsOptinYes?: string
  questionsOptinNo?: string
  questionsOptinEmail?: string
  questionsIntro?: string
  questionsIntroNew?: string
  questionsAnswered?: string
  enrichmentQuestionBirthday?: string
  enrichmentQuestionFirstname?: string
  enrichmentQuestionEmail?: string
  enrichmentQuestionGender?: string
  questionsNotAnswered?: string
  webappHomeTitle?: string
  webappHomeSubtitle?: string
  webappStoreSelectionTitle?: string | null
  webappStoreSelectionLabel?: string | null
  webappProfileTitle?: string | null
  webappProfileSubtitle?: string | null
  webappWelcomeButton?: string | null
  webappPhoneSubtitle?: string | null
  webappOptinTitle?: string | null
  webappOptinSubtitle?: string | null
  webappOptinYes?: string | null
  webappOptinNo?: string | null
}

export interface LoyaltyTexts {
  rewardsTitle: string
  rewardsSubtitle: string
  rewardsSubtitleNew: string
  rewardsText: string
  rewardsButton: string
  rewardsPopupConfirmTitle: string
  rewardsPopupConfirmSubtitle: string
  rewardsPopupForbiddenTitle: string
  name?: string
}

export interface TipsLink {
  id: number
  icon?: Icon
  title: string
  subtitle?: string
  link: string
}
export interface Upsell {
  store: string
  storeId: number
  packSms?: number
  packSmsId?: number
  price: number
}

export interface SmsPricing {
  smsCount: number
  pricePerSms: number
  pricePerMonth: number
  popular: boolean
}

export interface CampaignEmailContent {
  id: number
  public_id: string
  name: string
  is_template: boolean
  from_template_id: number | null
  created_at: string
  updated_at: string
}

export interface Campaign {
  id: number
  name: string
  type: CampaignType
  sentAt: string
  messageContent: string
  convertedCustomersNb: number
  generatedTurnover: number
  targetedRecipientsNb: number
  priceWithoutVat: number
  hasQrcode: boolean
  createdAt: string
  updatedAt: string
  isSent: boolean
  isStoreSenderName: boolean
  stores: storeInterface[]
  conversionDelay: number
  statistics?: CampaignStatistics
  shortLinks: ShortLink[]
  reward?: LoyaltyReward
  client?: Client
  emailContentId?: number | null
  emailContent?: CampaignEmailContent | null
  isDraft?: boolean
  status: CampaignStatus
  scope?: ContentScope | null
  creator?: ContentCreator | null
}

export type CampaignType = 'sms' | 'push' | 'email'
export type CampaignStatus = 'draft' | 'scheduled' | 'sent'
export type CampaignSortKey = 'updated_at' | 'created_at' | 'launch_date'

export interface CampaignChoices {
  defaultSlider: boolean
  points: {
    value: number[]
    max: number[]
  }
}

export interface CampaignSelection {
  name: string
  lists: []
  excludedLists: []
  stores: storeInterface[]
  ages: number[]
  checkins: number[]
  genders: string[]
  points: any[]
  frequencyTypes: []
  tags: []
  firstCheckin: {
    from: string
    to: string
  }
  lastVisit: {
    from: string
    to: string
  }
  surveyQuestionChoices: []
  contestGameIds: []
  countryAlpha2Code: string
}

export interface QrCodeProvider {
  isQrCodeUnique: Ref<boolean>
  updateQrCode: () => void
}

export interface CampaignClientTypes {
  type: 'each' | 'specific'
  title: string
  description: string
}

export interface CampaignTime {
  hour: number
  minute: number
}

export interface CampaignMessageTemplate {
  category: CampaignMessageCategory
  content: string
  id: number
  isActive: boolean
  title: string
}

export interface CampaignMessageCategory {
  id: number
  slug: string
  name: string
  emoji: string
  color: string
}

export interface CampaignStatistics {
  campaignId?: number
  messagesCount: number
  visitsCount: number
  visitsPercentage: number
  clicksPercentage: number
  clicksCount: number
  qrcodeCount: number
  qrcodePercentage: number
  generatedRevenue: number
  rewardRetrievedCount: number
  rewardRetrievedPercentage: number
  emailSent?: number
  emailDelivered?: number
  emailOpenedUnique?: number
  emailClicked?: number
  emailClickedUnique?: number
  emailBounced?: number
  emailSoftBounced?: number
  emailHardBounced?: number
  emailSpamComplaints?: number
  emailUnsubscribed?: number
}

export interface CustomerTag {
  id: number
  slug: string
  name: string
  type: string
  color: ChipColor
  isAssignableOnEdit: boolean
  isAssignableOnCreate: boolean
  isAssigned: boolean
}

export interface ConsumedProduct {
  id: number
  name: string
  percentage: number
}

export interface ProductWithPublicId {
  id: number
  publicId: string
  name: string
  description?: string
  price?: number
  image?: string
  type?: 'product' | 'menu'
  externalId?: string
}

export interface MerchantSmsPrice {
  country: {
    flag: string
    id: number
    name: string
  }
  sellingSmsPriceWithoutVat: string
}

export interface Client {
  id: number
  name: string
  registrationNumber: string
}

export interface CustomerFrequencyType {
  id: number
  slug: string
  name: string
  color: string
  isDefault: number
}

export type List = {
  id: number
  name: string
  merchantId: number
  createdAt: string
  updatedAt: string
}

export interface CustomerDetails {
  id: number
  averageTicketAmount?: number
  birthDate?: string | null
  campaignMessages?: any[] | null
  checkinCount: number
  customerMerchantProduct?: any[]
  email?: string | null
  loyaltyCardNumber?: string | null
  firstName?: string | null
  frequencyType: CustomerFrequencyType
  gender?: string | null
  hasOptin: boolean
  hasOptinEmail: boolean
  hasOrders?: boolean
  hasRetrievedImportedPoints: boolean
  lastName?: string | null
  lastValidCheckinDate: string
  lists?: List
  phone?: string | null
  points: number
  postalCode?: string | null
  publicId: string
  registerStore: {
    id: number
    name: string
  }
  referenceStore: {
    id: number
    name: string
  }
  referenceStoreId: number
  registerDate: string
  firstLogInToWebappAt: string | null
  enrollmentSource?: string | null
  tags: CustomerTag[]
  walletExternalId: string
  walletStatus: string
}

export type CustomerImportStage = 'pending' | 'parsing' | 'processing' | 'complete'

export interface CustomerImportListItem {
  isPending: boolean
  stage: CustomerImportStage
  isStalled: boolean
  stalledForMinutes: number | null
  expectedLines: number
  customerImport: {
    id: number
    name: string
    userName: string
    storeId: number | null
    hasOptin: boolean | null
    hasOptinEmail: boolean | null
    startedAt: string
    finishedAt: string | null
  }
  totalCustomersProcessed: number
  totalCustomers: number
  totalCustomersWithError: number
  newCustomersCount: number
  existedCustomersCount: number
  parsedLines: number
  percentage: number
  isComplete: boolean
  isLegacy: boolean
  uploadedAt: string
  lastActivityAt: string | null
  merchantSegment?: string
}

export type CustomerImportBlockedReasonCode =
  | 'pending_no_lines'
  | 'parsing_complete_not_started'
  | 'parsing_stopped'
  | 'processing_stopped'

export interface CustomerImportBlockedReason {
  code: CustomerImportBlockedReasonCode
  parsed?: number
  total?: number
  processed?: number
}

export interface CustomerImportDiagnostics {
  id: number
  name: string
  stage: CustomerImportStage
  isPending: boolean
  isComplete: boolean
  isStalled: boolean
  stalledForMinutes: number | null
  blockedReason: CustomerImportBlockedReason | null
  uploadedAt: string
  startedAt: string | null
  finishedAt: string | null
  lastActivityAt: string | null
  expectedLines: number
  percentage: number
  parsedLines: number
  totalCustomers: number
  totalCustomersProcessed: number
  totalCustomersWithError: number
  summary?: ImportSummary | null
  file: {
    path: string
    exists: boolean
    sizeBytes: number | null
  }
  errors: {
    message: string
    count: number
    sampleLines: number[]
  }[]
}

export interface CustomerImport {
  id: number
  name: string
  columns: CustomerImportColumn[]
  fileReadStatus?: CustomerImportFileReadStatus
  fileReadError?: string | null
  updatePointsOnExisting?: boolean
}

export type CustomerImportFileReadStatus = 'pending' | 'reading' | 'ready' | 'failed'

export interface CustomerImportColumn {
  index: number
  header: string | null
  type: string
  values: CustomerImportColumnValue[]
  rawFirstValues: (string | null)[]
}

export interface CustomerImportColumnValue {
  valueInFile: string
  mappedValue: string
}

export interface Step {
  id: string
  title?: string
  isComplete?: boolean
  href: string
  icon?: Icon
  stepOrder: number
  subtitle?: string
}

export type Icon = FunctionalComponent<HTMLAttributes & VNodeProps>

export interface SubItem {
  name?: string
  query?: Record<string, string>
  params?: Record<string, string>
  /** Extra param sets (same route name) that also mark this item active, e.g. detail sub-pages reached from it. */
  matchParams?: Record<string, string>[]
  matchRoutes?: { name: string; query?: Record<string, string | null> }[]
  excludeFromActive?: boolean
  text?: string
  translationKey?: string
  icon?: string
  isGroup?: boolean
  subItems?: SubItem[]
}

export interface AddonCard {
  id: number
  slug: string
  isAvailable: boolean
  cover?: string
  title: string
  description: string
  label: string
  actionButton: {
    text: string
    style: string
    link: string | null
  }
  content?: {
    title: string
    description: string
    label: string
    images: string[]
    actionButtons: AddonActionButtons[]
  }
}

export interface AddonConfirmationModal {
  id: number
  cover: string
  title: string
  description: string
  buttonText: string
}

interface AddonActionButtons {
  text: string
  link: string | null
  slug: string
  confirmationModalId: number
}

export interface TabletIdentifiers {
  email: string
  code: string
}

export interface MerchantMedias {
  logo: string
  logoSquare?: string
  logoMail?: string
  coverA: string
  coverB: string
  coverC: string
  coverD: string
}

export interface MerchantTabletImageForm {
  type: string
  isCroppable: boolean
  name: string
  label: string | null
  pictureUrl: string | null
  allowCrop: boolean
  rules: string
}

export interface MerchantConfigs {
  activityTypePhysical?: string
  bonusPoints?: string
  checkinPoints?: string
  colorCC?: string
  delayBetweenCheckins?: string
  displayTabletHomeButtonWithIntegration?: boolean
  employeeTabletCode?: string
  hasConfiguredAtLeastOneStore?: boolean
  hasConfiguredVisualIdentity?: boolean
  hasPointsByCurrency?: boolean
  loyaltyDeliveryOrderMessageChannel?: string
  loyaltyOrderMinimumAmount?: string
  loyaltyPointsByCurrencyUnit?: string
  questionInterval?: string
  rewardTimeout?: string
  tabletPointsClaimingDelay?: string
  hasWebappForceInfoCompletionAtRegistrer?: boolean
  hasPointsCanceledOnOrderCancelation?: boolean
  hasCompatibleVoucherIntegration?: boolean
  pointExpirationType?: 'point_relative' | 'after_registration' | 'absolute_date' | null
  pointExpirationUnit?: 'day' | 'month' | 'year' | null
  pointExpirationValue?: number | string | null
  pointExpirationStartsAt?: string | null
  pointExpirationRetroactive?: boolean
}

export interface LoyaltyPointsConfigForm {
  globals: LoyaltyPointsConfigSection
  perVisit: LoyaltyPointsConfigSection
  perAmountSpent: LoyaltyPointsConfigSection
}
export interface LoyaltyPointsConfigSection {
  title: string
  hasToggle?: boolean
  toggleValue?: boolean
  toggleDisabled?: boolean
  toggleTips?: InputFieldTip
  fields: {
    [key: string]: LoyaltyPointsConfigField
  }
}

type LoyaltyPointsConfigFieldBase = {
  label: string
  hasToolTip?: boolean
  required?: boolean
  disabled?: boolean
  minLength?: number
  maxLength?: number
  min?: number
  integerOnly?: boolean
  max?: number
  toolTip?: string
  tips?: InputFieldTip
  rules?: string
  isSupportConnectionOnly?: boolean
}

export type LoyaltyPointsConfigField =
  | (LoyaltyPointsConfigFieldBase & { type: 'toggle'; value: boolean })
  | (LoyaltyPointsConfigFieldBase & { type: 'text'; value: string })
  | (LoyaltyPointsConfigFieldBase & { type: 'number'; value: number })

export interface InputFieldTip {
  text: string
  link: {
    value: string
    isOpenedInNewTab?: boolean
  }
}

export interface LoyaltyNotificationsConfigFields {
  smsName: LoyaltyNotificationsConfigField<string>
  hasNotificationAfterOrder: LoyaltyNotificationsConfigField<boolean>
  loyaltyDeliveryOrderMessageChannel: LoyaltyDeliveryOrderMessageChannel
  orderReceivedTextsFields: OrderReceivedTextsFields
  smsAtSubscription: LoyaltyNotificationsConfigField<string>
}

interface LoyaltyNotificationsConfigField<T = string | boolean> {
  label: string
  value: T
  type: string
  required?: boolean
  disabled?: boolean
  minLength?: number
  maxLength?: number
  toolTip?: string
  tooltip?: Tooltip
}

export interface Tooltip {
  text: string
  show: boolean
}

export interface LoyaltyDeliveryOrderMessageChannel {
  options: RadioOption[]
  value: RadioOption
  type: string
  required: boolean
}

export interface OrderReceivedTextsFields {
  integrationAfterOrderMailSubject: IntegrationAfterOrderNotification
  integrationAfterOrderMailBody: IntegrationAfterOrderNotification
  integrationAfterOrderSms: IntegrationAfterOrderNotification
}

export interface IntegrationAfterOrderNotification {
  label: string
  value: string
  type: string
  notificationChannel: string
  rules: string
  maxLength?: number
  minLength?: number
  required?: boolean
  disabled?: boolean
  toolTip?: string
}

export type RulePrimitive = string | number | boolean | null
export type RuleValue = RulePrimitive | RulePrimitive[]

export interface AudienceStore {
  id: number
  name: string
  is_churned?: boolean
}

export interface Audience {
  id?: number
  name: string
  type: string
  frequency_type?: string
  template_used?: string
  rules?: AudienceRule[]
  icon?: Icons
  customer_count?: number
  is_processing?: boolean
  store_ids?: number[]
  stores?: AudienceStore[]
  merchant_stores?: AudienceStore[]
  scope?: ContentScope | null
  creator?: ContentCreator | null
}

export interface AudienceRule {
  operator: string
  value: RuleValue
  type?: string
  attribute?: string
  parameters?: RuleParameters
}
export type Rules =
  | 'age'
  | 'averageCheckinCount'
  | 'birthDate'
  | 'canReceivePushNotifications'
  | 'checkinCount'
  | 'city'
  | 'consumedProduct'
  | 'contestGameParticipation'
  | 'country'
  | 'email'
  | 'firstName'
  | 'frequencyType'
  | 'gender'
  | 'hasOptin'
  | 'hasOptinEmail'
  | 'lastName'
  | 'lastValidCheckin'
  | 'pointCumulated'
  | 'pointTotal'
  | 'registrationDate'
  | 'surveys'
  | 'tags'
  | 'webappLogIn'
export interface DateRange {
  start_date: string
  end_date: string
}

export interface RuleFilter {
  date_filter: {
    type: string
    operator: string
    value: RuleValue
  }
  quantity_filter?: {
    operator: string
    value: RuleValue
  }
}

export type VueTelInputPhoneObject = {
  countryCode: string
  country: string
  valid: boolean
  number: string
  formatted: string
  nationalNumber: string
}

export interface ImportValueMapping {
  type: string
  values: {
    valueInFile: string
    mappedValue: string
  }[]
}

export interface ImportSummary {
  newCustomersCount: number
  existedCustomersCount: number
  ignoredLinesCount: number
  noPhoneNewCustomersCount: number
}

export interface ImageFile {
  file?: File | null
  id: number
  path: string
  name?: string
  size?: number
  width?: number
  height?: number
  blob?: Blob | null
}

export interface TemplatableData {
  rewards: {
    id: number
    name: string
    description: string
    pictureUrl: string
    rewardType: {
      slug: string
    }
    storeIds: number[]
    includedSegmentIds?: number[]
    excludedSegmentIds?: number[]
    rewardTypeId?: number
  }[]
  configs: Record<string, unknown>
  medias: {
    coverA: string
    coverB: string
    coverC: string
    coverD: string
  }
}

export type RewardTypeSlug =
  | 'points'
  | 'free_product'
  | 'voucher_discount_percent'
  | 'voucher_discount_amount'
  | 'voucher_free_delivery'
  | 'voucher_x_for_y'
  | 'product_discount_percent'
  | 'product_discount_amount'
  | 'lost'

export interface MessageTemplate {
  content: string
  subject?: string
  messageChannelId: number
  messageChannel?: {
    id: number
  }
}
export interface ContestGameRewardSlot {
  dayFromStart: number | null
  dayOfWeek: string | null
  probability: number
}
export interface ContestGameReward {
  name: string
  points: number | null
  hasQrcode?: boolean
  probability: number
  color: string
  textColor: string
  notificationTextOverride: string | null
  rewardTypeSlug: RewardTypeSlug
  rewardTypeId?: number | null
  externalId?: string | null
  value?: any
  validityInDays?: number | null
  minimumOrderAmount?: number | null
  id?: number | null
  rewardId?: number | null
  pictureUrl?: string | null
  reward?: {
    id: number
    name: string
    pictureUrl: string
  }
  slots?: ContestGameRewardSlot[]
}
export interface Contest {
  id?: number
  name: string
  slug: string
  scope?: ContentScope | null
  isVisibleInWebapp: boolean
  fromDate: string | null
  toDate: string | null
  coverImage: string | null
  coverImageUrl?: string
  logo: string | null
  logoUrl?: string
  title: string
  subtitle: string
  askActionTitle: string
  askActionText: string
  askActionButtonText: string
  canLose: boolean
  losingProbability: number
  losingTileColor: string
  losingTileTextColor?: string
  losingTileText: string
  losingText: string
  winningText: string
  callToActionText: string
  formTitle: string
  formSubtitle: string
  formSubmitButtonText: string
  optinText: string
  contestGameRewards: ContestGameReward[]
  isPlayableManyTimes: boolean
  daysBetweenParticipations: number | string
  isClickRequiredToPlay: boolean
  messageContentPoints: string
  messageTemplate: MessageTemplate
  messageTemplateQrcode?: MessageTemplate | null
  stores: (string | Store)[]
  storesShortLinks?: Store[]
  losingNotificationText: string
  sendingNotificationText: string
  waitingValidationText: string
  targetUrl: string | null
  backgroundColor?: string
  buttonColor?: string
  textColor?: string
  buttonTextColor?: string
  defaultCover?: string
  conditionsUrl?: string
  contestGameBoostType?: {
    slug: string
  }
  mechanic?: ContestMechanic
  isGeneric?: boolean
  isEnabled?: boolean
  shortLink?: {
    shortLink?: string
  }
  winnerText?: string
  confirmationText?: string
  isLegacy?: boolean
  isOtpRequired?: boolean
  isBotDetectionEnabled?: boolean
  migratedFromContestGameId?: number | null
  migratedFromContestGame?: {
    id: number
    name: string
    isLegacy: boolean
  } | null
}

export interface ContestGame {
  id: string
  name: string
  isActive: boolean
  isPaused: number
  isLegacy: boolean
  participantsCounts: number
  stores: Array<{
    name: string
  }>
  contestGameBoostType: string
  fromDate: string
  toDate: string
  shortLink: string
  rewards?: Array<{
    id: number
    name: string
  }>
  isVisibleInWebapp?: boolean
  scope?: ContentScope | null
  creator?: ContentCreator | null
}

export type MissionType = 'wallet' | 'survey' | 'contest_game' | 'review' | 'crm' | 'loyalty_import'

export type MissionTranslations = Record<string, string>

export type WebappMission = {
  id: number | null
  type: MissionType | null
  title: MissionTranslations
  subtitle: MissionTranslations
  ctaLabel: MissionTranslations
  iconType: 'heroicon' | 'emoji'
  iconValue: string
  points: number
  destinationUrl: string | null
  instanceType: string | null
  instanceId: number | null
  isVisible: boolean
  order: number
}
